"use client";

import { useRouter } from "next/navigation";
import { useState, useEffect } from "react";
import { useAuth } from "./context/AuthContext";
import { acquireAccessToken, ADScope } from "./auth/authConfig";
import { SpaceBadge } from "./components/SpaceBadge";
import AssistantRow from "./components/AssistantRow";
import { apiFetch } from "./utils/apiFetch";

interface SpaceAssistant {
  name: string;
  display_name?: string;
  description?: string;
  authentication?: {
    enabled: boolean;
    allowed_groups?: string[];
    is_owner: boolean;
  };
  use_shared_workspace?: boolean;
  chat_attachments_enabled?: boolean;
  history_enabled?: boolean;
  tools?: Array<{ type?: string; datasource?: string; display_name?: string }>;
}

interface SpaceData {
  name: string;
  displayName?: string;
  assistants: SpaceAssistant[];
}

export default function Home() {
  const router = useRouter();
  const { isAuthenticated, isLoading: authLoading, login } = useAuth();
  const [spaces, setSpaces] = useState<SpaceData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      login();
    }
  }, [isAuthenticated, authLoading, login]);

  useEffect(() => {
    const fetchSpaces = async () => {
      try {
        setIsLoading(true);
        setError(null);

        let accessToken = "";
        try {
          accessToken = await acquireAccessToken(ADScope.AISearchApi);
        } catch (err) {
          console.error("Failed to acquire access token:", err);
        }

        // Root-cause guard (WI #10172473): do not call the backend before a
        // valid OAuth token is available. acquireAccessToken can return an
        // empty string when the token cannot be obtained silently — in that
        // case it triggers an interactive redirect (acquireTokenRedirect) or
        // there is no active account yet. Firing /api/spaces without an
        // Authorization header makes the BFF fan the request out to every
        // space backend, each rejecting it with 401 (the pre-auth 401
        // fan-out). Bail here and let the auth/redirect flow re-render once a
        // token is available.
        if (!accessToken) {
          console.warn(
            "Skipping /api/spaces fetch: OAuth token not yet acquired",
          );
          return;
        }

        const headers: Record<string, string> = {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        };

        const response = await apiFetch("/api/spaces", { headers });
        if (!response.ok) {
          throw new Error("Failed to fetch spaces");
        }

        const data = await response.json();
        setSpaces(data.spaces || []);
      } catch (err) {
        console.error("Error fetching spaces:", err);
        setError("Failed to load assistants. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };

    if (isAuthenticated && !authLoading) {
      fetchSpaces();
    }
  }, [isAuthenticated, authLoading]);

  const handleAgentClick = (agentName: string, spaceName: string) => {
    router.push(`/assistants/${spaceName}/${agentName}`);
  };

  const filterAgents = (assistants: SpaceAssistant[]): SpaceAssistant[] => {
    return assistants.filter((agent) => {
      // Role/group access is already enforced server-side (with proper
      // Graph-resolved nested-group membership) before an assistant ever
      // reaches this response — re-filtering here on the client would only
      // rely on the ID token's raw `groups` claim, which omits nested/
      // overage groups the backend already accounted for.
      if (agent.authentication?.enabled) {
        if (!isAuthenticated) return false;
      }
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const nameMatch = agent.name.toLowerCase().includes(q);
        const displayMatch = (agent.display_name || "")
          .toLowerCase()
          .includes(q);
        if (!nameMatch && !displayMatch) return false;
      }
      return true;
    });
  };

  if (authLoading) {
    return null;
  }

  if (!isAuthenticated) {
    return null;
  }

  const visibleSpaces = [...spaces]
    .filter((space) => !space.name.startsWith("xyz-"))
    .map((space) => ({ space, agents: filterAgents(space.assistants) }))
    .filter(({ agents }) => agents.length > 0)
    .sort((a, b) => b.agents.length - a.agents.length);

  const totalAssistants = visibleSpaces.reduce(
    (sum, { agents }) => sum + agents.length,
    0,
  );

  return (
    <div>
      <div
        className="flex items-baseline justify-between"
        style={{
          padding: "16px 24px 12px",
          borderBottom: "1px solid var(--border)",
        }}
      >
        <div className="flex items-baseline gap-3">
          <h1
            style={{
              margin: 0,
              fontSize: 18,
              fontWeight: 600,
              letterSpacing: "-0.01em",
            }}
          >
            Assistants
          </h1>
          {!isLoading && visibleSpaces.length > 0 && (
            <span
              className="mono"
              style={{ fontSize: 11.5, color: "var(--fg-tertiary)" }}
            >
              {visibleSpaces.length}{" "}
              {visibleSpaces.length === 1 ? "space" : "spaces"} ·{" "}
              {totalAssistants}{" "}
              {totalAssistants === 1 ? "assistant" : "assistants"}
            </span>
          )}
        </div>
        <div className="search">
          <svg
            className="ic ic-sm"
            viewBox="0 0 16 16"
            style={{ color: "var(--fg-tertiary)" }}
          >
            <circle cx="7" cy="7" r="4.5" />
            <path d="m10.5 10.5 3 3" />
          </svg>
          <input
            placeholder="Search assistants..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      <div style={{ padding: "8px 24px 32px" }}>
        {error && (
          <div
            className="flex items-center justify-between"
            style={{
              marginTop: 16,
              padding: "10px 14px",
              borderRadius: 4,
              border: "1px solid var(--border)",
              borderLeft: "2px solid var(--danger)",
              background: "var(--bg-subtle)",
            }}
          >
            <span style={{ fontSize: 12.5, color: "var(--danger)" }}>
              {error}
            </span>
            <button
              className="btn"
              style={{ height: 26 }}
              onClick={() => window.location.reload()}
            >
              Retry
            </button>
          </div>
        )}

        {isLoading && (
          <div style={{ marginTop: 18 }}>
            <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div
                  key={i}
                  className="animate-pulse rounded"
                  style={{
                    minHeight: 100,
                    border: "1px solid var(--border)",
                    background: "var(--bg-subtle)",
                  }}
                />
              ))}
            </div>
          </div>
        )}

        {!isLoading && visibleSpaces.length === 0 && !error && (
          <div
            style={{
              marginTop: 16,
              padding: "10px 14px",
              borderRadius: 4,
              border: "1px solid var(--border)",
              borderLeft: "2px solid var(--warning)",
              background: "var(--warning-bg)",
              fontSize: 12.5,
              color: "var(--fg-secondary)",
            }}
          >
            {searchQuery
              ? "No assistants match your search."
              : "No assistants are currently available."}
          </div>
        )}

        {!isLoading && visibleSpaces.length > 0 && (
          <div
            className="columns-1 md:columns-2 xl:columns-3"
            style={{ columnGap: 16, marginTop: 6 }}
          >
            {visibleSpaces.map(({ space, agents }) => {
              const sortedAgents = [...agents].sort((a, b) =>
                a.name.localeCompare(b.name),
              );
              const spaceLabel = space.displayName || space.name;
              return (
                <section
                  key={space.name}
                  className="mb-4 overflow-hidden rounded bg-bg"
                  style={{
                    breakInside: "avoid",
                    border: "1px solid var(--border)",
                  }}
                >
                  <button
                    onClick={() => router.push(`/assistants/${space.name}`)}
                    className="flex w-full items-center gap-2 text-left transition-colors hover:bg-bg-hover"
                    style={{
                      padding: "10px 14px",
                      background: "var(--bg-subtle)",
                      borderBottom: "1px solid var(--border)",
                    }}
                  >
                    <SpaceBadge name={spaceLabel} />
                    <span className="label-eyebrow">{spaceLabel}</span>
                    <span
                      className="mono"
                      style={{
                        marginLeft: "auto",
                        fontSize: 11,
                        color: "var(--fg-tertiary)",
                      }}
                    >
                      {sortedAgents.length}
                    </span>
                    <svg
                      className="ic ic-sm"
                      viewBox="0 0 16 16"
                      style={{ color: "var(--fg-quaternary)" }}
                    >
                      <path d="M6 3l5 5-5 5" />
                    </svg>
                  </button>
                  <div>
                    {sortedAgents.map((agent, idx) => (
                      <div
                        key={agent.name}
                        style={
                          idx > 0
                            ? { borderTop: "1px solid var(--border)" }
                            : undefined
                        }
                      >
                        <AssistantRow
                          name={agent.display_name || agent.name}
                          description={agent.description}
                          onClick={() =>
                            handleAgentClick(agent.name, space.name)
                          }
                          capabilities={{
                            useSharedWorkspace: agent.use_shared_workspace,
                            chatAttachmentsEnabled:
                              agent.chat_attachments_enabled,
                            authEnabled: agent.authentication?.enabled,
                            historyEnabled: agent.history_enabled,
                            toolsCount: agent.tools?.length,
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
